/**
 * Documentos de uma pasta, com as ações de quem gere (reindexar, ativar, mover,
 * excluir). Visualizar exige ler a pasta: `manage` sem o perfil lista o metadado e
 * não abre o conteúdo (spec §5.1). Visualizar abre as páginas em imagem, sem cópia;
 * baixar o documento inteiro fica dentro do visualizador, só para proprietário e
 * auditoria (spec 2026-09-27-kb-visualizar-sem-copia §5). O dossiê do Git leva o selo com o commit e só
 * pode ser excluído depois da fonte (spec KB-Git §11).
 *
 * Atalhos (spec 2026-10-22-kb-arquivo-repetido-e-atalho, §3) entram na mesma lista: abrem o
 * original pelas rotas de hoje, e sem leitura do original mostram cadeado e não abrem. O
 * atalho não libera leitura (A2): quem decide é a pasta do original, no backend.
 */
import { useState, type ReactNode } from 'react'
import { ActionIcon, Anchor, Badge, Button, Group, Menu, Modal, Select, Stack, Table, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  IconBrandGit,
  IconCheck,
  IconDotsVertical,
  IconEye,
  IconFolderOpen,
  IconFolderShare,
  IconFolderSymlink,
  IconLock,
  IconPlayerPause,
  IconRefresh,
  IconTrash,
  IconUnlink,
} from '@tabler/icons-react'
import {
  deleteKbDocument,
  listarAtalhosDoDocumento,
  reindexKbDocument,
  removerKbAtalho,
  updateKbDocument,
  type KbAtalho,
  type KbAtalhoDoDocumento,
  type KbDocument,
  type KbDocumentStatus,
  type KbFolderNode,
} from '@/api/kb'
import { VisualizadorDeDocumento } from '@/components/kb/VisualizadorDeDocumento'
import { mensagemDeErro } from './acesso'
import { bloqueioDeMover } from './arrastar'
import { useOrigemDeArraste } from './ArrastarESoltar'
import { caminhoDe } from './arvore'
import { nomeDaPasta } from './envio'
import { formatarData, motivoDaFalha, shaCurto, STATUS_DO_DOCUMENTO } from './formato'

/** "Gerado do Git · <commit> · <data>" (spec KB-Git §11); o commit leva ao GitLab. */
function SeloDoGit({ d }: { d: KbDocument }) {
  return (
    <Group gap={6} wrap="nowrap">
      <IconBrandGit size={12} stroke={1.5} aria-hidden />
      <Text size="xs" c="dimmed">Gerado do Git</Text>
      {d.git_commit && (
        <>
          <Text size="xs" c="dimmed" aria-hidden>·</Text>
          {d.git_commit_url ? (
            <Anchor href={d.git_commit_url} target="_blank" rel="noopener noreferrer" size="xs" ff="monospace">
              {shaCurto(d.git_commit)}
            </Anchor>
          ) : (
            <Text size="xs" ff="monospace">{shaCurto(d.git_commit)}</Text>
          )}
        </>
      )}
      {d.git_commit_em && (
        <>
          <Text size="xs" c="dimmed" aria-hidden>·</Text>
          <Text size="xs" ff="monospace">{formatarData(d.git_commit_em)}</Text>
        </>
      )}
    </Group>
  )
}

/** Com a fonte viva, a passada recriaria o dossiê: o backend recusa (409 `git_source_active`). */
function fonteViva(d: KbDocument): boolean {
  return d.origem === 'git' && d.git_estado != null && d.git_estado !== 'excluido'
}

/** Motivo de o documento não oferecer Excluir, ou `null` quando oferece. */
function bloqueioDeExclusao(d: KbDocument): string | null {
  if (d.origem === 'guia') return 'Este guia vem do repositório do ConsoleAI: ele sai da base quando sai do repositório.'
  if (fonteViva(d)) return 'Exclua a fonte em Repositórios para poder apagar este documento.'
  return null
}

/** A linha arrasta o documento para outra pasta (o menu continua oferecendo o mesmo). */
function LinhaDoDocumento({ d, children }: { d: KbDocument; children: ReactNode }) {
  const origem = useOrigemDeArraste({ tipo: 'documento', doc: d })
  return (
    <Table.Tr ref={origem.setNodeRef} {...origem.attributes} {...origem.listeners} style={{ opacity: origem.isDragging ? 0.5 : undefined }}>
      {children}
    </Table.Tr>
  )
}

/** O título do original; `null` vem quando a pasta dele é sensível e a pessoa não a lê (R1). */
export function tituloDoAtalho(a: KbAtalho): string {
  return a.titulo ?? 'Documento de pasta restrita'
}

/** A frase da confirmação de excluir um original com atalhos, ou `null` sem atalhos. */
export function avisoDosAtalhos(atalhos: KbAtalhoDoDocumento[]): string | null {
  if (atalhos.length === 0) return null
  const nomes = atalhos.flatMap((a) => (a.caminho ? [nomeDaPasta(a.caminho)] : []))
  const semNome = atalhos.length - nomes.length
  const pastas = (n: number) => `${n} ${n === 1 ? 'pasta' : 'pastas'} que você não acessa`
  const onde = nomes.length === 0
    ? ` em ${pastas(semNome)}`
    : ` em: ${nomes.join(', ')}${semNome > 0 ? ` e em mais ${pastas(semNome)}` : ''}`
  return `Este documento tem atalhos${onde}. Eles também serão removidos.`
}

interface PropsDoAtalho {
  a: KbAtalho
  gere: boolean
  onVer: () => void
  onIrParaOriginal: () => void
  onRemover: () => void
}

/** Linha de atalho: não arrasta (mover atalho é remover e criar outro, spec §2). */
function LinhaDoAtalho({ a, gere, onVer, onIrParaOriginal, onRemover }: PropsDoAtalho) {
  const titulo = tituloDoAtalho(a)
  const st = a.status ? (STATUS_DO_DOCUMENTO[a.status] ?? { label: a.status, color: 'gray' }) : null
  const caminho = a.pasta_original.caminho
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <IconFolderSymlink size={16} stroke={1.5} aria-label="Atalho" role="img" style={{ flexShrink: 0, marginTop: 2 }} />
          <Stack gap={0}>
            {a.liberado ? (
              <Anchor component="button" type="button" size="sm" fw={600} ta="left" c="inherit" onClick={onVer}>{titulo}</Anchor>
            ) : (
              <Text size="sm" fw={600} c="dimmed">{titulo}</Text>
            )}
            <Text size="xs" c="dimmed">
              {caminho ? `Atalho · original em ${caminho}` : 'Atalho · original numa pasta que você não acessa'}
            </Text>
            {!a.liberado && (
              <Group gap={4} wrap="nowrap">
                <IconLock size={12} stroke={1.5} aria-label="Sem acesso" role="img" />
                <Text size="xs" c="dimmed">Sem acesso ao original</Text>
              </Group>
            )}
          </Stack>
        </Group>
      </Table.Td>
      <Table.Td>{st ? <Badge size="sm" variant="light" color={st.color}>{st.label}</Badge> : <Text size="xs" c="dimmed">—</Text>}</Table.Td>
      <Table.Td><Text size="xs" c="dimmed">—</Text></Table.Td>
      <Table.Td><Text size="xs" c="dimmed">—</Text></Table.Td>
      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {a.liberado && (
            <Tooltip label="Visualizar o original">
              <ActionIcon variant="subtle" color="gray" aria-label={`Visualizar ${titulo}`} onClick={onVer}>
                <IconEye size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          )}
          {gere && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label={`Ações do atalho ${titulo}`}>
                  <IconDotsVertical size={16} stroke={1.5} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {caminho && (
                  <Menu.Item leftSection={<IconFolderOpen size={14} />} onClick={onIrParaOriginal}>Ir para o original</Menu.Item>
                )}
                <Menu.Item leftSection={<IconUnlink size={14} />} onClick={onRemover}>Remover atalho</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}

interface Props {
  documentos: KbDocument[]
  /** Atalhos da pasta; o backend só os manda quando a pasta é liberada */
  atalhos?: KbAtalho[]
  /** "Ir para o original" */
  onAbrirPasta: (id: number) => void
  /** A pessoa lê a pasta (perfil nela, ou vê tudo) */
  podeLer: boolean
  gere: boolean
  pastaId: number
  /** Pastas da mesma organização, destinos possíveis de "Mover" */
  destinos: KbFolderNode[]
}

export function DocumentosDaPasta({ documentos, atalhos = [], onAbrirPasta, podeLer, gere, pastaId, destinos }: Props) {
  const qc = useQueryClient()
  const [movendo, setMovendo] = useState<KbDocument | null>(null)
  const [destino, setDestino] = useState<string | null>(null)
  const [vendo, setVendo] = useState<number | null>(null)

  const aoMudar = (titulo: string, mensagem: string) => {
    qc.invalidateQueries({ queryKey: ['kb-documents'] })
    qc.invalidateQueries({ queryKey: ['kb-folder'] })
    qc.invalidateQueries({ queryKey: ['kb-folder-detalhes'] })
    notifications.show({ color: 'teal', title: titulo, message: mensagem })
  }
  const aoFalhar = (e: unknown) =>
    notifications.show({ color: 'red', title: 'Não foi possível concluir', message: mensagemDeErro(e, 'Falha na operação.') })

  const reindexar = useMutation({ mutationFn: (d: KbDocument) => reindexKbDocument(d.id), onSuccess: (_r, d) => aoMudar('Reindexação solicitada', d.titulo), onError: aoFalhar })
  const alternar = useMutation({
    // O PATCH não tem `enabled`: desativar é `status: 'disabled'`, e reativar é
    // reindexar (volta a `pending` e o worker indexa de novo).
    mutationFn: async (d: KbDocument): Promise<void> => {
      if (d.status === 'disabled') await reindexKbDocument(d.id)
      else await updateKbDocument(d.id, { status: 'disabled' })
    },
    onSuccess: (_r, d) => aoMudar(d.status === 'disabled' ? 'Documento ativado' : 'Documento desativado', d.titulo),
    onError: aoFalhar,
  })
  const excluir = useMutation({ mutationFn: (d: KbDocument) => deleteKbDocument(d.id), onSuccess: (_r, d) => aoMudar('Documento excluído', d.titulo), onError: aoFalhar })
  const removerAtalho = useMutation({
    mutationFn: (a: KbAtalho) => removerKbAtalho(a.id),
    onSuccess: (_r, a) => aoMudar('Atalho removido', tituloDoAtalho(a)),
    onError: aoFalhar,
  })
  const mover = useMutation({
    mutationFn: ({ d, para }: { d: KbDocument; para: number }) => updateKbDocument(d.id, { folder_id: para }),
    onSuccess: (_r, { d }) => { aoMudar('Documento movido', d.titulo); setMovendo(null); setDestino(null) },
    onError: aoFalhar,
  })

  // Os atalhos saem junto com o original (A3): a confirmação diz onde eles estão.
  const confirmarExclusao = async (d: KbDocument) => {
    let aviso: string | null
    try {
      aviso = avisoDosAtalhos(await listarAtalhosDoDocumento(d.id))
    } catch {
      aviso = 'Não foi possível conferir os atalhos deste documento: se houver, eles também serão removidos.'
    }
    modals.openConfirmModal({
      title: 'Excluir documento',
      children: (
        <Stack gap="xs">
          <Text size="sm">Excluir <strong>{d.titulo}</strong>? Os trechos indexados e o arquivo original são removidos.</Text>
          {aviso && <Text size="sm">{aviso}</Text>}
        </Stack>
      ),
      labels: { confirm: 'Excluir', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => excluir.mutate(d),
    })
  }

  const opcoesDeDestino = destinos
    .filter((p) => p.id !== pastaId)
    .map((p) => ({ value: String(p.id), label: caminhoDe(destinos, p.id) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

  return (
    <>
      <Table verticalSpacing="xs" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Documento</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Trechos</Table.Th>
            <Table.Th>Enviado em</Table.Th>
            <Table.Th aria-label="Ações" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {documentos.map((d) => {
            const st = STATUS_DO_DOCUMENTO[d.status] ?? { label: d.status, color: 'gray' }
            return (
              <LinhaDoDocumento key={d.id} d={d}>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>{d.titulo}</Text>
                    {d.origem === 'git' && <SeloDoGit d={d} />}
                    {d.status === 'failed' && d.erro && <Text size="xs" c="red">{motivoDaFalha(d.erro)}</Text>}
                  </Stack>
                </Table.Td>
                <Table.Td><Badge size="sm" variant="light" color={st.color}>{st.label}</Badge></Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{d.chunk_count}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{formatarData(d.created_at)}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    {podeLer && (
                      <Tooltip label="Visualizar">
                        <ActionIcon variant="subtle" color="gray" aria-label={`Visualizar ${d.titulo}`} onClick={() => setVendo(d.id)}>
                          <IconEye size={16} stroke={1.5} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {gere && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label={`Ações de ${d.titulo}`}>
                            <IconDotsVertical size={16} stroke={1.5} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {bloqueioDeMover(d) ? (
                            <Text size="xs" c="dimmed" px="sm" py={4} maw={240}>{bloqueioDeMover(d)}</Text>
                          ) : (
                            <Menu.Item leftSection={<IconFolderShare size={14} />} onClick={() => setMovendo(d)}>Mover para outra pasta</Menu.Item>
                          )}
                          <Menu.Item leftSection={<IconRefresh size={14} />} disabled={d.status === 'indexing'} onClick={() => reindexar.mutate(d)}>Reindexar</Menu.Item>
                          <Menu.Item
                            leftSection={d.status === 'disabled' ? <IconCheck size={14} /> : <IconPlayerPause size={14} />}
                            disabled={d.status === 'indexing'}
                            onClick={() => alternar.mutate(d)}
                          >
                            {d.status === 'disabled' ? 'Ativar' : 'Desativar'}
                          </Menu.Item>
                          <Menu.Divider />
                          {bloqueioDeExclusao(d) ? (
                            <Text size="xs" c="dimmed" px="sm" py={4} maw={240}>{bloqueioDeExclusao(d)}</Text>
                          ) : (
                            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => void confirmarExclusao(d)}>Excluir</Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Table.Td>
              </LinhaDoDocumento>
            )
          })}
          {atalhos.map((a) => (
            <LinhaDoAtalho
              key={`atalho-${a.id}`}
              a={a}
              gere={gere}
              onVer={() => a.document_id !== null && setVendo(a.document_id)}
              onIrParaOriginal={() => a.pasta_original.id !== null && onAbrirPasta(a.pasta_original.id)}
              onRemover={() => removerAtalho.mutate(a)}
            />
          ))}
        </Table.Tbody>
      </Table>

      <VisualizadorDeDocumento documentId={vendo} onClose={() => setVendo(null)} />

      <Modal opened={movendo !== null} onClose={() => { setMovendo(null); setDestino(null) }} title={<Text fw={700}>Mover documento</Text>}>
        <Stack gap="sm">
          <Text size="sm">{movendo?.titulo}</Text>
          <Select
            label="Pasta de destino"
            description="Quem lê o documento passa a ser quem lê a pasta de destino."
            data={opcoesDeDestino}
            value={destino}
            onChange={setDestino}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setMovendo(null); setDestino(null) }}>Cancelar</Button>
            <Button
              disabled={!destino}
              loading={mover.isPending}
              onClick={() => movendo && destino && mover.mutate({ d: movendo, para: Number(destino) })}
            >
              Mover
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
